import { Component, OnInit, signal, inject, ChangeDetectionStrategy } from "@angular/core";
import { LorenAsset } from "../../../loren-assets/interfaces/loren-asset.interface";
import { LorenAssetService } from "../../../loren-assets/services/loren-asset-service/loren-asset.service";
import { combineLatest } from "rxjs";
import { map, startWith, take, tap } from "rxjs/operators";
import { LorenBaseFeature } from "../../../../shared/base-components/loren-base-feature/loren-base-feature";
import { LorenBreakpointObserverService } from "../../../../shared/services/loren-breakpoint-observer-service/loren-breakpoint-observer.service";
import { LorenNavService } from "../../../../shared/modules/loren-nav/components/loren-nav/services/loren-nav-service/loren-nav.service";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { LorenGroupService } from "../../../loren-groups/services/loren-group-service/loren-group.service";
import { LorenUserSettingsService } from "../../services/loren-user-settings-service/loren-user-settings.service";
import { LorenAuthenticationService } from "../../../../shared/services/loren-authentication-service/loren-authentication.service";
import { LorenSnackBarService } from "../../../../shared/services/loren-snack-bar-service/loren-snack-bar.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: "loren-user-settings",
  templateUrl: "./loren-user-settings.component.html",
  styleUrls: ["./loren-user-settings.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LorenUserSettingsComponent extends LorenBaseFeature implements OnInit {
  protected breakpointObserverService: LorenBreakpointObserverService;
  protected lorenNavService: LorenNavService;
  private groupService = inject(LorenGroupService);
  private userSettingsService = inject(LorenUserSettingsService);
  private lorenSnackBarService = inject(LorenSnackBarService);
  private authService = inject(LorenAuthenticationService);
  private assetService = inject(LorenAssetService);

  currentlyFilteredAssets: LorenAsset[] = [];
  form: UntypedFormGroup;
  searchString: string;
  searchStringFormControl: UntypedFormControl;
  showAll = false;
  showThreshold = 5;
  subscribedAssets: number[] = [];

  filteredAssets = signal<LorenAsset[]>(null);
  loading = signal<boolean>(false);
  error = signal<boolean>(false);

  private userId: number;

  constructor() {
    const breakpointObserverService = inject(LorenBreakpointObserverService);
    const lorenNavService = inject(LorenNavService);

    super();
  
    this.breakpointObserverService = breakpointObserverService;
    this.lorenNavService = lorenNavService;
  }

  ngOnInit(): void {
    this.userId = Number(this.authService.getUserInfo().id);
    this.searchStringFormControl = new UntypedFormControl(this.searchString);
    this.loading.set(true);
    this.userSettingsService
      .getUserNotificationsSubscriptions(this.userId)
      .pipe(take(1))
      .subscribe({
        next: (x) => {
          this.loading.set(false);
          this.error.set(false);
          this.subscribedAssets = x.items;
        },
        error: () => {
          this.loading.set(false);
          this.error.set(true);
        },
      });
    combineLatest([
      this.assetService.query(),
      this.groupService.query(),
      this.searchStringFormControl.valueChanges.pipe(startWith("")),
    ])
      .pipe(
        map(([assets, groups, filter]) => {
          assets.items.forEach(
            (x) =>
              (x.groups = groups.items
                .filter((g) => x.belongsToGroups.includes(g.id))
                .map((g) => g.name))
          );
          if (filter) {
            return assets.items.filter(
              (x) =>
                x.name.toLowerCase().includes(filter.toLowerCase()) ||
                x.description?.toLowerCase().includes(filter.toLowerCase()) ||
                x.groups?.join(" ").toLowerCase().includes(filter.toLowerCase())
            );
          }

          return assets.items;
        }),
        tap((filteredAssets) => (this.currentlyFilteredAssets = filteredAssets))
      )
      .subscribe((res) => this.filteredAssets.set(res));

    this.form = new UntypedFormGroup({});
    this.form.addControl("searchString", this.searchStringFormControl);
  }

  onSearchSubmit() {
    this.searchString = this.form.value.searchString;
  }

  allChecked = (assets: LorenAsset[]) => {
    return (
      assets.some((x) => this.subscribedAssets.includes(x.id) === false) ===
      false
    );
  };

  toggleSubscription(asset: LorenAsset) {
    const idx = this.subscribedAssets.findIndex((x) => x === asset.id);

    if (idx < 0) {
      this.subscribedAssets.push(asset.id);
    } else {
      this.subscribedAssets.splice(idx, 1);
    }

    this.pushChanges();
  }

  toggleAllSubscriptions($event) {
    if ($event.checked) {
      const assetsToAdd = this.currentlyFilteredAssets
        .filter((x) => !this.subscribedAssets.includes(x.id))
        .map((x) => x.id);
      this.subscribedAssets.push(...assetsToAdd);
    } else {
      const assetsToRemove = this.currentlyFilteredAssets
        .filter((x) => this.subscribedAssets.includes(x.id))
        .map((x) => x.id);
      assetsToRemove.forEach((x) => {
        this.subscribedAssets.splice(
          this.subscribedAssets.findIndex((s) => s === x),
          1
        );
      });
    }

    this.pushChanges();
  }

  private pushChanges() {
    this.userSettingsService
        .saveUserNotificationsSubscriptions(this.userId, this.subscribedAssets)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.lorenSnackBarService.showSuccessMessage();
        });
  }
}
